import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo } from 'react';
import { ButtonGroup, Dropdown } from 'react-bootstrap';
import { AppErrorBoundaryComponent } from '../../../../components/error-boundary/app-error-boundary.component';
import { FILTER_NAMES } from '../../../../components/filters/hooks/filter-names.constants';
import {
  DRILLTHROUGH_FULL_VIEW_MAP,
  useUpdateDrillthrough,
} from '../../../../components/filters/hooks/use-filter-drillthrough-selection.hook';
import { RowCheckboxComponent } from '../../../../components/reports/components/row-checkbox.component';
import { AppTabularColumn } from '../../../../components/reports/dtos/app-tabular.dto';
import { GenericTableComponent } from '../../../../components/reports/generic-table/generic-table.component';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import {
  FilterSelectedValue,
  OPERATIONS,
  VALUE_MATCH,
} from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useAppFilterSelectedValue } from '../../../../hooks/filters/use-app-filter-selected-value.hook';
import { IMAGES_REPO } from '../../../../static-resources/home-image-repo';
import { formatBigNumber } from '../../../../utils/formatting/number-formatter.helper';
import { useSwitchShowDetails } from '../../../layout/hooks/switch-panel.hook';
import { CompactViewProgressBarComponent } from '../compact-view/compact-view-progress-bar.component';
import { COMPACT_VIEW_LEVELS } from '../compact-view/constants/compact-view-levels.constants';
import './full-table-view.component.scss';

const columnHelper = createColumnHelper<Record<string, any>>();

const parseMeasuresColumns = (state: AppTabularColumn[], data: any) => {
  if (state === undefined) return [];
  return state.map((col: AppTabularColumn) => {
    return columnHelper.display({
      id: col.id,
      header: col.name,
      size: 180,

      cell: (props) => (
        <div className="full-view-figure">
          <div className="full-view-bar">
            <CompactViewProgressBarComponent
              min={0}
              max={data?.max}
              value={props.row.original[col.id]}
            />
          </div>
          <div className="full-view-measure">
            {formatBigNumber(props.row.original[col.id])}
          </div>
        </div>
      ),
    });
  });
};

const getSelection = (filterId: string, values: any[]) => {
  const selection: FilterSelectedValue = {
    id: filterId,
    values,
    matchStrategy: {
      operation: OPERATIONS.AND,
      valueMatch: VALUE_MATCH.MULTIPLE,
      groupName: 'group-1',
    },
  };
  return selection;
};

export interface FullViewComponentProps extends ReportsComponentProps {
  totalReportId: string;
  enabled?: boolean;
  excludeMode?: boolean;
}

export const FullTableViewComponent = ({
  reportName: reportId,
  totalReportId,
  enabled,
  filterSelection,
  className,
  excludeMode,
}: FullViewComponentProps) => {
  const { selectedValue: categoryValue, setSelectedValue: setCategoryValue } =
    useAppFilterSelectedValue(FILTER_NAMES.COMPACT_VIEW_CATEGORY);
  const { selectedValue: brandValue, setSelectedValue: setBrandValue } =
    useAppFilterSelectedValue(FILTER_NAMES.COMPACT_VIEW_BRAND);
  const { selectedValue: subBrandValue, setSelectedValue: setSubBrandValue } =
    useAppFilterSelectedValue(FILTER_NAMES.COMPACT_VIEW_SUBBRAND);
  const { selectedValue: sizeGroupValue, setSelectedValue: setSizeGroupValue } =
    useAppFilterSelectedValue(FILTER_NAMES.COMPACT_VIEW_SIZEGROUP);
  const { selectedValue: ppgValue, setSelectedValue: setPpgValue } =
    useAppFilterSelectedValue(FILTER_NAMES.COMPACT_VIEW_PPG);

  const { setShow: setShowDetails } = useSwitchShowDetails('d', false);
  const { setShow: setShowDecomp } = useSwitchShowDetails('d', false, '/t/i');

  useEffect(() => {
    if (!enabled) return;

    if (filterSelection && filterSelection.length > 0) {
      COMPACT_VIEW_LEVELS.forEach((levelData) => {
        const { filterId } = levelData;
        let setSelection = null;
        switch (filterId) {
          case FILTER_NAMES.COMPACT_VIEW_CATEGORY:
            setSelection = setCategoryValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_BRAND:
            setSelection = setBrandValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_SUBBRAND:
            setSelection = setSubBrandValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_SIZEGROUP:
            setSelection = setSizeGroupValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_PPG:
            setSelection = setPpgValue;
            break;
        }
        if (setSelection) {
          const selection: FilterSelectedValue = getSelection(filterId, []);
          setSelection(selection);
        }
      });
    }
  }, [
    filterSelection,
    setCategoryValue,
    setBrandValue,
    setSubBrandValue,
    setSizeGroupValue,
    setPpgValue,
    enabled,
    excludeMode,
  ]);
  const selectRowHandler = useCallback(
    (row: Record<string, any>, checked?: boolean) => {
      if (!enabled) return;

      const isChecked =
        (!checked && excludeMode) || (checked && excludeMode === false);

      COMPACT_VIEW_LEVELS.forEach((levelData) => {
        const { valueFieldName, labelFieldName, filterId } = levelData;
        let selRows: any[] = [];
        let setSelection = null;
        switch (filterId) {
          case FILTER_NAMES.COMPACT_VIEW_CATEGORY:
            selRows = categoryValue?.values ?? [];
            setSelection = excludeMode ? undefined : setCategoryValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_BRAND:
            selRows = brandValue?.values ?? [];
            setSelection = excludeMode ? undefined : setBrandValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_SUBBRAND:
            selRows = subBrandValue?.values ?? [];
            setSelection = excludeMode ? undefined : setSubBrandValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_SIZEGROUP:
            selRows = sizeGroupValue?.values ?? [];
            setSelection = excludeMode ? undefined : setSizeGroupValue;
            break;
          case FILTER_NAMES.COMPACT_VIEW_PPG:
            selRows = ppgValue?.values ?? [];
            setSelection = setPpgValue;
            break;
        }
        if (setSelection) {
          if (isChecked) {
            if (row[valueFieldName] !== undefined) {
              const found = selRows.find(
                (r) => r.value === row[valueFieldName],
              );
              if (!found) {
                const newArr = selRows.concat([
                  {
                    value: row[valueFieldName],
                    label: row[labelFieldName],
                    exclude: excludeMode,
                  },
                ]);
                const selection = getSelection(filterId, newArr);
                setSelection(selection);
              }
            }
          } else {
            const newArr = selRows.filter(
              (r) => r.value !== row[valueFieldName],
            );
            if (newArr) {
              const selection = getSelection(filterId, newArr);
              setSelection(selection);
            }
          }
        }
      });
    },
    [
      setCategoryValue,
      categoryValue,
      setBrandValue,
      brandValue,
      setSubBrandValue,
      subBrandValue,
      setSizeGroupValue,
      sizeGroupValue,
      setPpgValue,
      ppgValue,
      excludeMode,
      enabled,
    ],
  );

  const isChecked = useCallback(
    (row: Record<string, any>): boolean => {
      if (!enabled) return false;

      if (ppgValue && ppgValue.values) {
        const val = row['IRIS_PPG_ID'];
        const checked =
          ppgValue.values.find((i) => i.value === val) !== undefined;
        return excludeMode ? !checked : checked;
      }
      return excludeMode ? true : false;
    },
    [ppgValue, excludeMode, enabled],
  );

  const updateDrillthrough = useUpdateDrillthrough(DRILLTHROUGH_FULL_VIEW_MAP);

  const drillThroughHandler = useCallback(
    (row: Record<string, any>) => {
      const drillthrouhSelection: FilterSelectedValue[] = [];
      COMPACT_VIEW_LEVELS.forEach((levelData) => {
        const { valueFieldName, labelFieldName, filterId } = levelData;
        if (row[valueFieldName] !== undefined) {
          drillthrouhSelection.push(
            getSelection(filterId, [
              { value: row[valueFieldName], label: row[labelFieldName] },
            ]),
          );
        }
      });
      setShowDetails(true);
      updateDrillthrough(filterSelection.concat(drillthrouhSelection));
    },
    [updateDrillthrough, filterSelection, setShowDetails],
  );

  const handleDecomp = useCallback(
    (row: Record<string, any>) => {
      const decompSelection: FilterSelectedValue[] = [];
      COMPACT_VIEW_LEVELS.forEach((levelData) => {
        const { valueFieldName, labelFieldName, filterId } = levelData;
        if (row[valueFieldName] !== undefined) {
          decompSelection.push(
            getSelection(filterId, [
              { value: row[valueFieldName], label: row[labelFieldName] },
            ]),
          );
        }
      });
      setShowDecomp(true);
      updateDrillthrough(filterSelection.concat(decompSelection));
    },
    [updateDrillthrough, filterSelection, setShowDecomp],
  );

  const endColumns = useMemo(() => {
    return [
      columnHelper.display({
        id: 'drillthough-action',
        size: 150,
        cell: (props: any) => (
          <div className="full-view-drillthrough-action">
            <Dropdown id="reports-button" title="Reports" as={ButtonGroup}>
              <Dropdown.Toggle
                variant="sm"
                className="reports-navigation-button"
                id="reports-button-toggle"
              >
                <img src={IMAGES_REPO.DemandButtonIcon} alt="" />
              </Dropdown.Toggle>
              <Dropdown.Menu align={'end'}>
                <Dropdown.Item
                  onClick={() => drillThroughHandler(props.row.original)}
                >
                  Drill Through
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleDecomp(props.row.original)}>
                  Decomp
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        ),
      }),
    ];
  }, [drillThroughHandler, handleDecomp]);

  const parseColumns = useMemo(
    () => (state: AppTabularColumn[]) => {
      if (state === undefined) return [];
      return state.map((col: AppTabularColumn, index: number) => {
        const accessorFn = (row: any) => row[col.id];

        return {
          header: col.name,
          enableSorting: true,
          id: col.id,
          size: index === 0 ? 160 : 150,
          accessorFn,
          cell: (props: any) => (
            <div className="full-view-cell">
              {index === 0 && (
                <div className="full-view-select-action">
                  <RowCheckboxComponent
                    isLastLevel={false}
                    data={props.row.original}
                    selectRowHandler={selectRowHandler}
                    isCheckedCallback={isChecked}
                  />
                </div>
              )}
              <div className="full-view-cell-text">
                {props.row.original[col.id]}
              </div>
            </div>
          ),
        };
      });
    },
    [selectRowHandler, isChecked],
  );

  return (
    <div className={`full-view-table ${className}`}>
      <AppErrorBoundaryComponent>
        <GenericTableComponent
          enabled={enabled}
          height={400}
          reportName={reportId}
          totalReportName={totalReportId}
          filterSelection={filterSelection}
          endColumns={endColumns}
          parseColumns={parseColumns}
          parseMeasuresColumns={parseMeasuresColumns}
        />
      </AppErrorBoundaryComponent>
    </div>
  );
};
